// Access Point add/edit page.
// Add mode: parent location comes from ?parentLocationId=... query string
// (set by the "Add AP" button inside a Location detail) or, if absent, from
// a dropdown selector inside the form (top-level Add Access Point flow).

import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {ChipSelector} from '../../components/common/ChipSelector'
import {FormField} from '../../components/common/FormField'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {showToast} from '../../components/common/Toast'
import {supabase, DEFAULT_MINE_ID} from '../../app/supabaseClient'
import {AP_STATUSES, IPV4_REGEX, MAC_REGEX, generateId} from '../../lib/constants'
import type {APDetail, APStatus} from './types'

interface FormErrors {
  name?: string
  ssid?: string
  mac_address?: string
  ip_address?: string
  x?: string
  y?: string
  z?: string
  radius_m?: string
  parent?: string
}

interface LocationOption {
  id: string
  name: string
  x: number | null
  y: number | null
  z: number | null
}

function parseNumeric(s: string): number | null {
  const t = s.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

export const APFormScreen: React.FC = () => {
  const {id} = useParams<{id?: string}>()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const [name, setName] = useState('')
  const [ssid, setSsid] = useState('')
  const [macAddress, setMacAddress] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [status, setStatus] = useState<APStatus>('active')
  // is_active is now a GENERATED column on the DB; no UI state needed.
  const [x, setX] = useState('')
  const [y, setY] = useState('')
  const [z, setZ] = useState('')
  const [level, setLevel] = useState('')
  const [radiusM, setRadiusM] = useState('50')

  // Locations list — needed when no parentLocationId query string in add mode.
  const [locations, setLocations] = useState<LocationOption[]>([])
  const [parentLocationId, setParentLocationId] = useState<string>('')

  const presetParentId = search.get('parentLocationId') ?? ''

  // Fetch locations + (in edit) the AP row.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data: locs} = await supabase
        .from('locations')
        .select('id, name, x, y, z')
        .order('name')
      const locList = (locs ?? []) as LocationOption[]
      if (cancelled) return
      setLocations(locList)

      if (isEdit && id) {
        const {data, error} = await supabase
          .from('access_points')
          .select(
            'id, location_id, name, mac_address, ssid, ip_address, is_active, status, ' +
              'x, y, z, level, radius_m',
          )
          .eq('id', id)
          .single<APDetail>()
        if (cancelled) return
        if (error || !data) {
          showToast(`Load failed: ${error?.message ?? 'Not found'}`, 'error')
          navigate('/access-points')
          return
        }
        setName(data.name)
        setSsid(data.ssid ?? '')
        setMacAddress(data.mac_address ?? '')
        setIpAddress(data.ip_address ?? '')
        setStatus(data.status)
        // is_active is derived from status; nothing to load here.
        setX(data.x != null ? String(data.x) : '')
        setY(data.y != null ? String(data.y) : '')
        setZ(data.z != null ? String(data.z) : '')
        setLevel(data.level != null ? String(data.level) : '')
        setRadiusM(data.radius_m != null ? String(data.radius_m) : '50')
        setParentLocationId(data.location_id ?? '')
      } else {
        // Add mode: pick parent (preset → query string; else first location).
        const initial =
          presetParentId && locList.some(l => l.id === presetParentId)
            ? presetParentId
            : locList[0]?.id ?? ''
        setParentLocationId(initial)
        const parent = locList.find(l => l.id === initial)
        if (parent) {
          setX(parent.x != null ? String(parent.x) : '')
          setY(parent.y != null ? String(parent.y) : '')
          setZ(parent.z != null ? String(parent.z) : '')
        }
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isEdit, navigate, presetParentId])

  // When parent changes in add mode (and presetParentId is empty), re-seed coords.
  const parentLockedByQuery = useMemo(() => Boolean(presetParentId), [presetParentId])
  useEffect(() => {
    if (isEdit || parentLockedByQuery || !parentLocationId) return
    const parent = locations.find(l => l.id === parentLocationId)
    if (!parent) return
    if (parent.x != null) setX(String(parent.x))
    if (parent.y != null) setY(String(parent.y))
    if (parent.z != null) setZ(String(parent.z))
  }, [parentLocationId, locations, isEdit, parentLockedByQuery])

  const validate = (): boolean => {
    const e: FormErrors = {}
    if (!name.trim()) e.name = 'AP name is required'
    if (!ssid.trim()) e.ssid = 'SSID is required'
    if (!macAddress.trim()) {
      e.mac_address = 'MAC address is required'
    } else if (!MAC_REGEX.test(macAddress.trim())) {
      e.mac_address = 'Must be a valid MAC (AA:BB:CC:DD:EE:FF)'
    }
    if (ipAddress.trim() && !IPV4_REGEX.test(ipAddress.trim())) {
      e.ip_address = 'Must be a valid IPv4 address'
    }
    if (radiusM.trim() && parseNumeric(radiusM) == null) {
      e.radius_m = 'Radius must be numeric'
    }
    // parentLocationId is REQUIRED on add (every AP must hang off a location);
    // OPTIONAL on edit (admins can detach an AP — empty value means
    // location_id = null in the DB).
    if (!isEdit && !parentLocationId) {
      e.parent = 'Pick a parent location'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setBusy(true)
    const now = new Date().toISOString()
    // `is_active` is a GENERATED column derived from `status` (patch_004) —
    // never write it. Postgres rejects with "cannot insert into generated
    // column" if it appears in INSERT/UPDATE payloads.
    const payload = {
      name: name.trim(),
      ssid: ssid.trim(),
      mac_address: macAddress.trim(),
      ip_address: ipAddress.trim() || null,
      status,
      x: parseNumeric(x),
      y: parseNumeric(y),
      z: parseNumeric(z),
      level: parseNumeric(level),
      radius_m: parseNumeric(radiusM) ?? 50,
      updated_at: now,
    }
    if (!isEdit) {
      const newId = generateId()
      const {error} = await supabase.from('access_points').insert({
        id: newId,
        mine_id: DEFAULT_MINE_ID,
        location_id: parentLocationId,
        ...payload,
        created_at: now,
      })
      if (error) {
        showToast(`Add failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Access point added.', 'success')
    } else {
      // In edit mode, also write location_id so the user can reassign or
      // unassign (empty string → null). Add mode already includes it
      // explicitly above.
      const {error} = await supabase
        .from('access_points')
        .update({...payload, location_id: parentLocationId || null})
        .eq('id', id!)
      if (error) {
        showToast(`Update failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Access point updated.', 'success')
    }
    setBusy(false)
    // Return to the parent location detail if we came from there, else to the AP list.
    if (!isEdit && parentLockedByQuery) navigate(`/locations/${parentLocationId}`)
    else navigate('/access-points')
  }

  const handleDelete = async () => {
    if (!id) return
    if (!window.confirm(`Delete access point "${name}"?`)) return
    setBusy(true)
    const {error} = await supabase.from('access_points').delete().eq('id', id)
    setBusy(false)
    if (error) {
      showToast(`Delete failed: ${error.message}`, 'error')
      return
    }
    showToast('Access point deleted.', 'success')
    navigate('/access-points')
  }

  const title = isEdit ? `Edit ${name || 'Access Point'}` : 'Add Access Point'
  const saveLabel = isEdit ? 'Save Changes' : 'Add AP'
  const backTo =
    !isEdit && parentLockedByQuery && parentLocationId
      ? `/locations/${parentLocationId}`
      : '/access-points'

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={title} backTo={backTo} />
      <PageShell
        footer={
          <>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="mr-auto rounded-md border border-statusRed px-lg py-sm text-md text-statusRed hover:bg-statusRed hover:text-white disabled:opacity-40"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(backTo)}
              disabled={busy}
              className="rounded-md border border-surfaceBorder px-lg py-sm text-md text-textSecondary hover:border-textPrimary hover:text-textPrimary disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-md bg-primary px-lg py-sm text-md font-bold text-black hover:bg-primaryLight disabled:opacity-40"
            >
              {busy ? 'Saving…' : saveLabel}
            </button>
          </>
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : (
          <div className="flex flex-col gap-lg">
            {/* Parent Location — required on add, optional on edit. The
                empty option lets admins detach an AP from any location. */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-textMuted">
                Parent Location {!isEdit && <span className="text-statusRed">*</span>}
              </label>
              <select
                value={parentLocationId}
                onChange={ev => setParentLocationId(ev.target.value)}
                disabled={parentLockedByQuery}
                className="mt-xs w-full rounded-md border border-surfaceBorder bg-surface px-md py-sm text-md text-textPrimary disabled:opacity-60"
              >
                {/* In edit mode: allow empty (unassigned). In add: must pick. */}
                {isEdit && <option value="">— Unassigned —</option>}
                {locations.length === 0 ? (
                  <option value="">— No locations —</option>
                ) : (
                  locations.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))
                )}
              </select>
              {errors.parent && (
                <div className="mt-xs text-xs text-statusRed">{errors.parent}</div>
              )}
              {isEdit && (
                <div className="mt-xs text-xs text-textMuted">
                  Choose a different location to reassign, or pick
                  "— Unassigned —" to detach this AP.
                </div>
              )}
            </div>

            <div className="flex gap-lg">
              <FormField
                label="Name"
                value={name}
                onChange={setName}
                placeholder="e.g. AP-VS-FFS-700-N"
                error={errors.name ?? null}
              />
              <FormField
                label="SSID"
                value={ssid}
                onChange={setSsid}
                placeholder="e.g. MINE-VS-700"
                error={errors.ssid ?? null}
              />
            </div>
            <div className="flex gap-lg">
              <FormField
                label="MAC Address"
                value={macAddress}
                onChange={setMacAddress}
                placeholder="AA:BB:CC:DD:EE:FF"
                error={errors.mac_address ?? null}
              />
              <FormField
                label="IP Address"
                value={ipAddress}
                onChange={setIpAddress}
                placeholder="192.168.1.100"
                error={errors.ip_address ?? null}
              />
            </div>

            <ChipSelector label="Status" options={AP_STATUSES} value={status} onChange={setStatus} />

            <div className="text-sm font-semibold uppercase tracking-wider text-textMuted">3D Position</div>
            <div className="flex gap-lg">
              <FormField label="X" value={x} onChange={setX} placeholder="0.0" error={errors.x ?? null} />
              <FormField label="Y" value={y} onChange={setY} placeholder="0.0" error={errors.y ?? null} />
              <FormField label="Z" value={z} onChange={setZ} placeholder="0.0" error={errors.z ?? null} />
            </div>
            <div className="flex gap-lg">
              <FormField label="Level" value={level} onChange={setLevel} placeholder="e.g. 1500" />
              <FormField
                label="Coverage Radius (m)"
                value={radiusM}
                onChange={setRadiusM}
                placeholder="50"
                error={errors.radius_m ?? null}
              />
            </div>
          </div>
        )}
      </PageShell>
    </main>
  )
}
